import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Button,
  ErrorSummary,
  Input,
  Modal,
  ModalFooter,
  SearchableSelect,
} from '@/components/common';
import { applyServerErrors } from '@/utils/formErrors';
import { phoneSchema, digitsOnlyOnChange, nameOnChange } from '@/utils/validation';
import api from '@/services/api';

/**
 * Board #203 -- Standardize Insurer Employee Creation.
 *
 * Same shape as `pages/users/UserFormPage` (the standard Add User form): the
 * same zod rules (`phoneSchema`, an 8-character password floor), the same
 * `digitsOnlyOnChange` / `nameOnChange` input behaviour, the same
 * `applyServerErrors` + `<ErrorSummary>` handling of a rejected save. The one
 * difference is where the account goes: the insurer picked here IS the
 * tenant, so this posts to the insurer's own provisioning endpoint rather
 * than to `/users/` with a tenant_type/tenant_id pair.
 *
 * What it replaces asked for an insurer and an EXISTING user id, so an admin
 * had to go create the account elsewhere first, give it the right role by
 * hand, and come back to map it.
 *
 * 2026-09-18 batch, item 3b/4 -- "Only Insurer Admins can be created, not
 * Insurer Employees". This modal used to offer Staff/Manager/Admin; both
 * Staff and Manager map onto the `insurer_employee` login on the backend
 * (`InsurerUserCreateSerializer.USER_ROLE_BY_ROLE`), which the report asked
 * to stop being creatable. The role picker is gone and every submission is
 * an Insurer Admin -- this is the same fix the Insurance Company detail
 * page's own Create User modal got, applied here because this modal is a
 * second, still-routed path (`/admin/insurers/employees`) to the exact same
 * `create_user` endpoint. Production's 4 live insurer_employee logins are
 * untouched: this only stops new ones being created from the UI.
 */

const schema = z.object({
  insurer: z.string().min(1, 'Insurer is required'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, 'Email is required').email('Invalid email'),
  phone: phoneSchema,
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type FormData = z.infer<typeof schema>;

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

interface InsurerOption {
  id: string;
  name: string;
}

export function InsurerEmployeeCreateModal({ onClose, onCreated }: Props) {
  const [serverMessages, setServerMessages] = useState<string[]>([]);

  // 100 is this API's max_page_size — the same reasoning as `FkSelect` in
  // AdminCrudFormModal: a picker that silently offers the first 20 of 30
  // insurers reads as "we don't have that insurer".
  const { data, isLoading } = useQuery({
    queryKey: ['fk-options', '/insurers/insurers/?page_size=100'],
    queryFn: async () => {
      const response = await api.get('/insurers/insurers/?page_size=100');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const insurerOptions = useMemo(() => {
    const rows: InsurerOption[] = Array.isArray(data) ? data : (data?.results ?? []);
    return rows.map((row) => ({ value: String(row.id), label: String(row.name ?? row.id) }));
  }, [data]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { insurer: '' },
  });

  const onSubmit = async (values: FormData) => {
    setServerMessages([]);
    try {
      // `role` is deliberately not sent: the backend defaults `create_user`
      // to "ADMIN" when it is omitted, and this modal no longer offers any
      // other choice.
      await api.post(`/insurers/insurers/${values.insurer}/create_user/`, {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
        password: values.password,
      });
    } catch (error) {
      // No toast.success anywhere above this line: the account either exists
      // now or this form stays open saying why it does not.
      setServerMessages(applyServerErrors(error, (name, err) => setError(name as keyof FormData, err)));
      return;
    }
    toast.success(`${values.first_name} ${values.last_name} added to the insurer as an Insurer Admin`);
    onCreated();
  };

  return (
    <Modal isOpen onClose={onClose} title="Create Insurer Admin" size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ErrorSummary messages={serverMessages} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <SearchableSelect
              label="Insurer"
              options={insurerOptions}
              value={watch('insurer') || ''}
              onChange={(value) => {
                setValue('insurer', value, { shouldValidate: true });
                clearErrors('insurer');
              }}
              required
              error={errors.insurer?.message}
              placeholder={isLoading ? 'Loading...' : 'Select insurer'}
            />
          </div>
          <Input
            label="First Name"
            required
            error={errors.first_name?.message}
            {...register('first_name', { onChange: nameOnChange })}
          />
          <Input
            label="Last Name"
            required
            error={errors.last_name?.message}
            {...register('last_name', { onChange: nameOnChange })}
          />
          <Input label="Email" type="email" required error={errors.email?.message} {...register('email')} />
          <Input
            label="Phone"
            required
            error={errors.phone?.message}
            {...register('phone', { onChange: digitsOnlyOnChange })}
          />
          <Input
            label="Password"
            type="password"
            required
            error={errors.password?.message}
            helperText="They should change this on first login."
            {...register('password')}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Created as an Insurer Admin for the selected insurer — this endpoint no longer creates
          any other insurer role.
        </p>
        <ModalFooter>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" isLoading={isSubmitting}>
            Create
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export default InsurerEmployeeCreateModal;
